<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="rules"
    class="login-form"
    @keyup.enter="handleSubmit"
  >
    <el-form-item prop="phone">
      <el-input
        v-model="form.phone"
        placeholder="请输入手机号"
        size="large"
        :prefix-icon="Phone"
        maxlength="11"
      />
    </el-form-item>

    <el-form-item prop="password">
      <el-input
        v-model="form.password"
        type="password"
        placeholder="请输入密码"
        size="large"
        :prefix-icon="Lock"
        show-password
      />
    </el-form-item>

    <el-form-item prop="captchaCode">
      <div class="captcha-row">
        <el-input
          v-model="form.captchaCode"
          placeholder="请输入图形验证码"
          size="large"
          :prefix-icon="CircleCheck"
          maxlength="6"
          class="captcha-input"
        />
        <div
          class="captcha-image"
          :class="{ loading: captchaLoading }"
          @click="refreshCaptcha"
        >
          <img v-if="captchaImage" :src="captchaImage" alt="验证码" />
          <el-icon v-else><Loading /></el-icon>
        </div>
      </div>
    </el-form-item>

    <div class="form-options">
      <el-checkbox v-model="form.remember">记住我</el-checkbox>
      <el-button link type="primary" @click="emit('forgotPassword')">
        忘记密码？
      </el-button>
    </div>

    <el-button
      type="primary"
      size="large"
      class="submit-btn"
      :loading="loading"
      @click="handleSubmit"
    >
      登录
    </el-button>
  </el-form>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Phone, Lock, CircleCheck, Loading } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'
import { loginByPassword } from '@/api/auth'
import { useUserStore } from '@/stores/user'
import { useCaptcha } from '@/composables/useCaptcha'

const emit = defineEmits<{
  success: []
  forgotPassword: []
}>()

const userStore = useUserStore()
const { captchaLoading, captchaImage, captchaKey, refreshCaptcha } = useCaptcha()
const formRef = ref<FormInstance>()
const loading = ref(false)

const form = ref({
  phone: '',
  password: '',
  captchaCode: '',
  remember: false,
})

const rules: FormRules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' },
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码至少6位', trigger: 'blur' },
  ],
  captchaCode: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
}

const handleSubmit = async () => {
  if (!formRef.value) return

  await formRef.value.validate(async (valid) => {
    if (!valid) return

    loading.value = true
    try {
      const res = await loginByPassword({
        phone: form.value.phone,
        password: form.value.password,
        captchaCode: form.value.captchaCode,
        captchaKey: captchaKey.value,
      })

      // 转换后端返回数据为前端用户状态
      const userState = {
        userId: res.data.userId,
        realName: res.data.realName,
        phone: res.data.phone,
        avatar: res.data.avatar,
        isVip: res.data.isVip,
        vipExpireTime: res.data.vipExpireTime,
      }

      userStore.setUserInfo(userState)
      userStore.setToken(res.data.accessToken)
      userStore.setRefreshToken(res.data.refreshToken)

      if (form.value.remember) {
        localStorage.setItem('remember_phone', form.value.phone)
      } else {
        localStorage.removeItem('remember_phone')
      }

      emit('success')
    } catch (error: any) {
      // 验证码错误时刷新验证码
      if (error.response?.data?.code === 400) {
        refreshCaptcha()
      }
    } finally {
      loading.value = false
    }
  })
}

onMounted(() => {
  refreshCaptcha()
  const rememberedPhone = localStorage.getItem('remember_phone')
  if (rememberedPhone) {
    form.value.phone = rememberedPhone
    form.value.remember = true
  }
})
</script>

<style scoped lang="scss">
.login-form {
  :deep(.el-input__inner) {
    height: 44px;
  }
}

.captcha-row {
  display: flex;
  gap: 12px;

  .captcha-input {
    flex: 1;
  }
}

.captcha-image {
  width: 120px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: var(--bg-color);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-color);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.loading {
    cursor: not-allowed;
  }
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
}
</style>
